import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";

interface ValueCard {
  image: string;
  imageAlt: string;
  heading: string;
  description: string;
}

const cards: ValueCard[] = [
  {
    image: "/assets/medallion_on_headstone.webp",
    imageAlt: "A Pontis QR medallion placed on a dark granite headstone",
    heading: "A medallion marks the place",
    description:
      "Placed on the headstone by the monument company. It's small, dignified, and permanent. A quiet invitation to remember.",
  },
  {
    image: "/assets/scan-anywhere.webp",
    imageAlt: "A smartphone displaying a Pontis memorial page after scanning the medallion",
    heading: "Anyone can scan, anywhere",
    description:
      "A grandchild visiting for the first time. A friend passing through town. Anyone with a phone can connect instantly — whether they're standing at the headstone or remembering from home.",
  },
  {
    image: "/assets/memorial-profile-mobile.png",
    imageAlt: "A Pontis memorial page showing photos, stories, and tributes from family members",
    heading: "Their story comes alive",
    description:
      "Photos from every chapter. Stories from the people who loved them. A living tribute that grows as family and friends add their memories.",
  },
  {
    image: "/assets/flowersdelivered.webp",
    imageAlt: "A fresh bouquet of flowers placed at the base of a headstone",
    heading: "Flowers on the dates that matter",
    description:
      "Birthdays, anniversaries, holidays. Seasonal deliveries arrive at their resting place — even when family can't be there in person.",
  },
  {
    image: "/assets/peoplevisiting.webp",
    imageAlt: "A young woman at a cemetery discovering a loved one's story on her phone",
    heading: "Generations will know who they were",
    description:
      "A great-grandchild visiting for the first time. A stranger passing through. Anyone who stops at that headstone will know more than just a name — they'll know the person.",
  },
];

export const BeforeAfterSection = () => {
  const prefersReducedMotion = useReducedMotion();

  // Desktop whileInView animations
  const fadeUp = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-100px" },
        transition: { duration: 0.6, ease: "easeOut" },
      };

  const imageScale = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, scale: 0.98 },
        whileInView: { opacity: 1, scale: 1 },
        viewport: { once: true, margin: "-100px" },
        transition: { duration: 0.8, ease: "easeOut" },
      };

  // Mobile scroll-driven animation — hooks always called
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // 7-phase scroll timing across 500vh
  // Phase 1 (0.00–0.14): headstone alone
  // Phase 2 (0.14–0.28): medallion fades in
  // Phase 3 (0.28–0.42): phone slides up, screen 1
  // Phase 4 (0.42–0.56): phone screen 2 (gallery)
  // Phase 5 (0.56–0.70): phone screen 3 (stories)
  // Phase 6 (0.70–0.80): phone screen 4 (tributes)
  // Phase 7 (0.80–1.00): phone fades out, scan-anywhere image fades in

  const headstoneOpacity   = useTransform(scrollYProgress, [0.28, 0.38], [1, 0]);
  const medallionOpacity   = useTransform(scrollYProgress, [0.12, 0.20, 0.28, 0.38], [0, 1, 1, 0]);
  const medallionScale     = useTransform(scrollYProgress, [0.12, 0.20], [0.5, 1]);
  const phoneY             = useTransform(scrollYProgress, [0.28, 0.40], ["100vh", "0px"]);
  const phoneOpacity       = useTransform(scrollYProgress, [0.28, 0.36, 0.76, 0.82], [0, 1, 1, 0]);
  const phase7ImageOpacity = useTransform(scrollYProgress, [0.78, 0.84], [0, 1]);

  const caption1Opacity = useTransform(scrollYProgress, [0.00, 0.03, 0.12, 0.14], [0, 1, 1, 0]);
  const caption2Opacity = useTransform(scrollYProgress, [0.14, 0.17, 0.26, 0.28], [0, 1, 1, 0]);
  const caption3Opacity = useTransform(scrollYProgress, [0.30, 0.34, 0.40, 0.42], [0, 1, 1, 0]);
  const caption4Opacity = useTransform(scrollYProgress, [0.42, 0.45, 0.54, 0.56], [0, 1, 1, 0]);
  const caption5Opacity = useTransform(scrollYProgress, [0.56, 0.59, 0.68, 0.70], [0, 1, 1, 0]);
  const caption6Opacity = useTransform(scrollYProgress, [0.70, 0.73, 0.78, 0.80], [0, 1, 1, 0]);
  const caption7Opacity = useTransform(scrollYProgress, [0.82, 0.86, 1.0, 1.0], [0, 1, 1, 1]);

  // Phone screen cross-fades — each fades in and stays (stacked, no flash-back to screen 1)
  const screen2Opacity = useTransform(scrollYProgress, [0.42, 0.46], [0, 1]);
  const screen3Opacity = useTransform(scrollYProgress, [0.56, 0.60], [0, 1]);
  const screen4Opacity = useTransform(scrollYProgress, [0.70, 0.74], [0, 1]);

  return (
    <section aria-label="How Pontis works" className="bg-[#FAF8F3]">

      {/* ── Shared section header (desktop only — mobile header lives inside the sticky viewport) ── */}
      <div className="hidden lg:block max-w-7xl mx-auto px-6 lg:px-8 pt-20">
        <motion.div {...fadeUp} className="text-center mb-6 lg:mb-16">
          <span className="text-sm tracking-wide uppercase text-brand-secondary">
            The Difference
          </span>
          <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-brand-text-primary mt-2">
            A headstone tells you where they rest. Pontis tells you who they were.
          </h2>
          <p className="text-lg text-brand-secondary mt-4 max-w-2xl mx-auto">
            On the left, a traditional headstone. On the right, what Pontis adds to it.
          </p>
        </motion.div>
      </div>

      {/* ── Mobile / tablet cinematic animation (< lg) ── */}
      {prefersReducedMotion ? (
        // Reduced-motion fallback: 7 phases stacked statically
        <div className="lg:hidden space-y-16 px-6 pb-16">
          {/* Header */}
          <div className="text-center pt-10">
            <span className="text-xs tracking-wide uppercase text-brand-secondary">
              From Stone to Story
            </span>
            <h2 className="font-serif text-2xl font-semibold text-brand-text-primary mt-1">
              Watch what a small medallion can do.
            </h2>
          </div>
          {/* Phase 1: bare headstone */}
          <div className="flex flex-col items-center gap-4">
            <img
              src="/assets/memorial-headstone.png"
              alt="A headstone in a peaceful cemetery"
              loading="lazy"
              className="w-full max-w-[280px] rounded-2xl shadow-[0_8px_30px_rgba(61,53,48,0.12)]"
            />
            <p className="font-serif text-xl text-brand-text-primary text-center">
              Most headstones tell you <em>when</em> someone lived.
            </p>
          </div>
          {/* Phase 2: headstone + medallion */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative max-w-[280px] w-full">
              <img
                src="/assets/memorial-headstone.png"
                alt="A headstone with a Pontis medallion"
                loading="lazy"
                className="w-full rounded-2xl shadow-[0_8px_30px_rgba(61,53,48,0.12)]"
              />
              <img
                src="/assets/pontis-medallion.png"
                alt="Pontis medallion on the headstone"
                loading="lazy"
                className="absolute top-[24%] right-[14%] w-[15%] drop-shadow-lg"
              />
            </div>
            <p className="font-serif text-xl text-brand-text-primary text-center">
              A Pontis medallion shows you <em>how</em> they lived.
            </p>
          </div>
          {/* Phases 3-6: phone mockups */}
          {[
            { src: "/assets/memorial-profile-mobile.png", alt: "A Pontis memorial page showing photos and tributes", caption: "One scan. Their whole story opens." },
            { src: "/assets/photo_5012981894437604242_y.jpg", alt: "Memorial photo gallery", caption: "Photos from every chapter of their life." },
            { src: "/assets/photo_5012981894437604243_y.jpg", alt: "Memorial stories page", caption: "Memories from the people who knew them best." },
            { src: "/assets/photo_5012981894437604241_y.jpg", alt: "Memorial tributes page", caption: "A legacy that never stops growing." },
          ].map(({ src, alt, caption }) => (
            <div key={src} className="flex flex-col items-center gap-4">
              <div className="relative w-[150px] drop-shadow-2xl">
                <div className="relative bg-[#1c1c1e] rounded-[44px] p-[10px] ring-1 ring-white/10">
                  <div className="relative overflow-hidden rounded-[36px] bg-black">
                    <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[72px] h-[22px] bg-black rounded-full z-10" />
                    <img src={src} alt={alt} loading="lazy" className="w-full" />
                    <div className="absolute bottom-[8px] left-1/2 -translate-x-1/2 w-[80px] h-[4px] bg-white/30 rounded-full" />
                  </div>
                </div>
                <div className="absolute left-[-4px] top-[88px] w-[4px] h-[26px] bg-[#252527] rounded-l-sm" />
                <div className="absolute left-[-4px] top-[124px] w-[4px] h-[42px] bg-[#252527] rounded-l-sm" />
                <div className="absolute left-[-4px] top-[174px] w-[4px] h-[42px] bg-[#252527] rounded-l-sm" />
                <div className="absolute right-[-4px] top-[116px] w-[4px] h-[58px] bg-[#252527] rounded-r-sm" />
              </div>
              <p className="font-serif text-xl text-brand-text-primary text-center">{caption}</p>
            </div>
          ))}
          {/* Phase 7: scan-anywhere full image */}
          <div className="flex flex-col items-center gap-4">
            <img
              src="/assets/scan-anywhere.webp"
              alt="A woman visiting a headstone and another remembering from home"
              loading="lazy"
              className="w-full max-w-[280px] rounded-2xl shadow-[0_8px_30px_rgba(61,53,48,0.12)]"
            />
            <p className="font-serif text-xl text-brand-text-primary text-center">
              Visit from home. Share from anywhere. Forever.
            </p>
          </div>
        </div>
      ) : (
        // Scroll-driven cinematic
        <div ref={containerRef} className="lg:hidden h-[500vh]">
          <div className="sticky top-14 h-[calc(100vh-56px)] flex flex-col overflow-hidden bg-[#FAF8F3] px-6">

            {/* Section header — normal flow, sits at top with small padding */}
            <div className="pt-8 pb-2 text-center px-4 shrink-0">
              <span className="text-xs tracking-wide uppercase text-brand-secondary">
                From Stone to Story
              </span>
              <h2 className="font-serif text-2xl font-semibold text-brand-text-primary mt-1">
                Watch what a small medallion can do.
              </h2>
            </div>

            {/* Animation area — fills remaining height below header */}
            <div className="relative flex-1">

            {/* Headstone layer — fades out as phone phase starts */}
            <motion.div
              style={{ opacity: headstoneOpacity }}
              className="absolute inset-0 flex items-start justify-center pt-6"
            >
              <div className="relative max-w-[280px] w-full">
                <img
                  src="/assets/memorial-headstone.png"
                  alt="A headstone in a peaceful cemetery"
                  className="w-full rounded-2xl shadow-[0_8px_30px_rgba(61,53,48,0.12)]"
                />
                <motion.img
                  src="/assets/pontis-medallion.png"
                  alt="Pontis medallion being placed on the headstone"
                  style={{ opacity: medallionOpacity, scale: medallionScale }}
                  className="absolute top-[24%] right-[14%] w-[15%] drop-shadow-lg"
                />
              </div>
            </motion.div>

            {/* Phase 7 image layer — fades in as phone fades out */}
            <motion.div
              style={{ opacity: phase7ImageOpacity }}
              className="absolute inset-0 flex items-center justify-center pb-32"
            >
              <img
                src="/assets/scan-anywhere.webp"
                alt="A woman visiting a headstone and another remembering from home"
                className="w-full max-w-[280px] rounded-2xl shadow-[0_8px_30px_rgba(61,53,48,0.12)]"
              />
            </motion.div>

            {/* Captions layer */}
            <div className="absolute bottom-[10%] left-0 right-0 flex justify-center">
              <div className="relative min-h-[6rem] w-full max-w-[320px]">
                <motion.p
                  style={{ opacity: caption1Opacity }}
                  className="absolute inset-0 font-serif text-xl text-brand-text-primary text-center leading-tight"
                >
                  Most headstones tell you <em>when</em> someone lived.
                </motion.p>
                <motion.p
                  style={{ opacity: caption2Opacity }}
                  className="absolute inset-0 font-serif text-xl text-brand-text-primary text-center leading-tight"
                >
                  A Pontis medallion shows you <em>how</em> they lived.
                </motion.p>
                <motion.p
                  style={{ opacity: caption3Opacity }}
                  className="absolute inset-0 font-serif text-xl text-brand-text-primary text-center leading-tight"
                >
                  One scan. Their whole story opens.
                </motion.p>
                <motion.p
                  style={{ opacity: caption4Opacity }}
                  className="absolute inset-0 font-serif text-xl text-brand-text-primary text-center leading-tight"
                >
                  Photos from every chapter of their life.
                </motion.p>
                <motion.p
                  style={{ opacity: caption5Opacity }}
                  className="absolute inset-0 font-serif text-xl text-brand-text-primary text-center leading-tight"
                >
                  Memories from the people who knew them best.
                </motion.p>
                <motion.p
                  style={{ opacity: caption6Opacity }}
                  className="absolute inset-0 font-serif text-xl text-brand-text-primary text-center leading-tight"
                >
                  A legacy that never stops growing.
                </motion.p>
                <motion.p
                  style={{ opacity: caption7Opacity }}
                  className="absolute inset-0 font-serif text-xl text-brand-text-primary text-center leading-tight"
                >
                  Visit from home. Share from anywhere. Forever.
                </motion.p>
              </div>
            </div>

            {/* Phone layer — slides up from 100vh, no headstone overlap */}
            <motion.div
              style={{ y: phoneY, opacity: phoneOpacity }}
              className="absolute inset-0 flex items-start justify-center pt-6"
            >
              {/* iPhone frame */}
              <div className="relative w-[150px] drop-shadow-2xl">
                <div className="relative bg-[#1c1c1e] rounded-[44px] p-[10px] ring-1 ring-white/10">
                  <div className="relative overflow-hidden rounded-[36px] bg-black">
                    {/* Dynamic Island */}
                    <div className="absolute top-[10px] left-1/2 -translate-x-1/2 w-[72px] h-[22px] bg-black rounded-full z-10" />
                    {/* Stacked screens — cross-fade on scroll */}
                    <div className="relative">
                      <img
                        src="/assets/memorial-profile-mobile.png"
                        alt="A Pontis memorial page showing photos and tributes"
                        className="w-full"
                      />
                      <motion.img
                        src="/assets/photo_5012981894437604242_y.jpg"
                        alt="Memorial page screen 2"
                        style={{ opacity: screen2Opacity }}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <motion.img
                        src="/assets/photo_5012981894437604243_y.jpg"
                        alt="Memorial page screen 3"
                        style={{ opacity: screen3Opacity }}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <motion.img
                        src="/assets/photo_5012981894437604241_y.jpg"
                        alt="Memorial page screen 4"
                        style={{ opacity: screen4Opacity }}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    </div>
                    {/* Home indicator */}
                    <div className="absolute bottom-[8px] left-1/2 -translate-x-1/2 w-[80px] h-[4px] bg-white/30 rounded-full" />
                  </div>
                </div>
                {/* Volume buttons */}
                <div className="absolute left-[-4px] top-[88px] w-[4px] h-[26px] bg-[#252527] rounded-l-sm" />
                <div className="absolute left-[-4px] top-[124px] w-[4px] h-[42px] bg-[#252527] rounded-l-sm" />
                <div className="absolute left-[-4px] top-[174px] w-[4px] h-[42px] bg-[#252527] rounded-l-sm" />
                {/* Power button */}
                <div className="absolute right-[-4px] top-[116px] w-[4px] h-[58px] bg-[#252527] rounded-r-sm" />
              </div>
            </motion.div>

            </div>{/* end animation area */}
          </div>
        </div>
      )}

      {/* ── Desktop / tablet sticky cards (lg+) — unchanged ── */}
      <div className="hidden lg:grid grid-cols-2 gap-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">

        {/* Left: sticky headstone photo */}
        <div className="sticky top-24 self-start">
          <div className="pb-6">
            <p className="text-lg md:text-xl font-serif italic text-brand-secondary text-center">
              A traditional headstone
            </p>
          </div>
          <img
            src="/assets/memorial-headstone.png"
            alt="A peaceful cemetery headstone"
            loading="lazy"
            className="w-full aspect-[4/5] max-h-[70vh] object-contain rounded-2xl shadow-[0_8px_30px_rgba(61,53,48,0.12)]"
          />
          <p className="mt-3 text-sm text-brand-secondary italic font-serif text-center">
            A headstone marks where they rest
          </p>
        </div>

        {/* Right: value cards */}
        <div>
          <div className="pb-6">
            <p className="text-lg md:text-xl font-serif italic text-brand-accent text-center">
              A headstone with Pontis
            </p>
          </div>
          <div className="space-y-16">
          {cards.map((card) => (
            <motion.div key={card.heading} {...fadeUp} className="space-y-4">
              <motion.img
                src={card.image}
                alt={card.imageAlt}
                loading="lazy"
                {...imageScale}
                className="w-full max-h-[450px] object-contain rounded-xl shadow-[0_4px_20px_rgba(61,53,48,0.08)]"
              />
              <h3 className="font-serif text-xl lg:text-2xl font-semibold text-brand-text-primary">
                {card.heading}
              </h3>
              <p className="text-base text-brand-secondary leading-relaxed">
                {card.description}
              </p>
            </motion.div>
          ))}
          </div>
        </div>
      </div>

    </section>
  );
};
